import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MessageCircle, X, Send, Sparkles, Maximize2, Minimize2, ShieldAlert, Check,
  ListChecks, TrendingUp, AlertTriangle, CheckCheck, GraduationCap, FolderKanban, Code, Copy,
  History, Plus, Trash2, Pencil, ArrowLeft, CircleCheck, CircleX
} from 'lucide-react';
import { ejecutarAccionNexus, ETIQUETA_ACCION, AccionNexus } from '../../services/nexusAccionesService.ts';
import { addDoc, query, orderBy, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import {
  ConversacionNexus, escucharConversaciones, migrarConversacionLegado, crearConversacion, tocarConversacion,
  renombrarConversacion, borrarConversacion, mensajesRef, mensajeRef
} from '../../services/nexusChatService.ts';
import { confirmarEnvioEstatusFacturaPrd } from '../../services/estatusFacturaBotService.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { useUserProjects } from '../../context/ProjectsContext.tsx';
import { esProyectoSap } from '../../pages/Proyectos/modulosConfig.ts';
import MarkdownNexus from './MarkdownNexus.tsx';

interface Sugerencia { icon: JSX.Element; label: string; text: string }

// Mismo formato que monthlyQuota.js (mes en UTC, "YYYY-MM").
const mesActualUTC = () => new Date().toISOString().slice(0, 7);

// Última conversación abierta, por usuario (comodidad local; si falla, arranca una nueva).
const claveChat = (uid: string) => `onehub:nexusChat:${uid}`;
const leerChatGuardado = (uid: string): string | null => {
  try { return localStorage.getItem(claveChat(uid)); } catch { return null; }
};
const guardarChat = (uid: string, chatId: string | null) => {
  try {
    if (chatId) localStorage.setItem(claveChat(uid), chatId);
    else localStorage.removeItem(claveChat(uid));
  } catch { /* modo privado: no se recuerda */ }
};

const fechaCorta = (ts: any): string => {
  const d: Date | null = ts?.toDate ? ts.toDate() : null;
  if (!d) return '';
  const hoy = new Date();
  if (d.toDateString() === hoy.toDateString()) return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  const ayer = new Date(hoy.getTime() - 86400000);
  if (d.toDateString() === ayer.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
};

/**
 * Sugerencias según dónde está el usuario (N2, 2026-10-27): dentro de un
 * proyecto, sobre ESE proyecto (Nexus lo toma como "este proyecto"); fuera,
 * sobre todos. Las de SAP solo en proyectos con ese módulo.
 */
const sugerenciasPara = (dentroDeProyecto: boolean, sap: boolean): Sugerencia[] => {
  if (!dentroDeProyecto) {
    return [
      { icon: <ListChecks size={14} className="text-blue-400" />, label: "¿Qué tengo pendiente?", text: "¿Qué tengo pendiente en mis proyectos?" },
      { icon: <CheckCheck size={14} className="text-emerald-400" />, label: "¿Qué debo aprobar?", text: "¿Tengo algo por aprobar?" },
      { icon: <FolderKanban size={14} className="text-purple-400" />, label: "Mis proyectos", text: "¿Cuáles son mis proyectos?" },
      { icon: <AlertTriangle size={14} className="text-amber-400" />, label: "¿Algo vencido?", text: "¿Tengo tareas vencidas?" }
    ];
  }
  const base: Sugerencia[] = [
    { icon: <ListChecks size={14} className="text-blue-400" />, label: "Mis pendientes aquí", text: "¿Qué tengo pendiente en este proyecto?" },
    { icon: <TrendingUp size={14} className="text-emerald-400" />, label: "¿Cómo va el proyecto?", text: "¿Cómo va este proyecto?" },
    { icon: <AlertTriangle size={14} className="text-amber-400" />, label: "¿Qué está atrasado?", text: "¿Qué está atrasado o vencido en este proyecto?" },
    { icon: <Plus size={14} className="text-sky-400" />, label: "Crear una tarea", text: "Quiero crear una tarea en este proyecto" }
  ];
  return base.concat(sap
    ? [{ icon: <Code size={14} className="text-purple-400" />, label: "Error 401 Ariba", text: "Tengo un error 401 Unauthorized en las APIs de Ariba, ¿qué reviso?" }]
    : [{ icon: <GraduationCap size={14} className="text-purple-400" />, label: "¿Cómo se hace…?", text: "¿Qué guías de Aprendizaje hay en este proyecto?" }]);
};

export default function GeminiChat({ user }: { user: any }) {
  const { profile } = useAuth();
  const { proyectos } = useUserProjects();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<any[]>([]);
  const [copiadoId, setCopiadoId] = useState<string | null>(null);
  // N3: varias conversaciones. `chatId` null = conversación nueva, que se crea
  // recién con el primer mensaje (así no quedan conversaciones vacías).
  const [chatId, setChatIdState] = useState<string | null>(() => (user ? leerChatGuardado(user.uid) : null));
  const [conversaciones, setConversaciones] = useState<ConversacionNexus[]>([]);
  const [verLista, setVerLista] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [tituloEdit, setTituloEdit] = useState('');
  // Código PRD del widget especial de "enviarEstatusFactura" — nunca se
  // envía como mensaje de chat ni pasa por Gemini (ver
  // estatusFacturaBotService.ts / geminiService.js, decisión de Fase 0).
  const [prdCodeInputs, setPrdCodeInputs] = useState<Record<string, string>>({});
  const [prdErrors, setPrdErrors] = useState<Record<string, string>>({});
  const [confirmandoPrdId, setConfirmandoPrdId] = useState<string | null>(null);
  // N4: acción propuesta por Nexus que se está ejecutando (id del mensaje).
  const [ejecutandoId, setEjecutandoId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const projectIdActual = location.pathname.match(/^\/proyecto\/([^/]+)/)?.[1] || null;
  const proyectoActual = projectIdActual ? proyectos.find((p) => p.id === projectIdActual) : undefined;
  const sugerencias = useMemo(
    () => sugerenciasPara(!!projectIdActual, !!proyectoActual && esProyectoSap(proyectoActual)),
    [projectIdActual, proyectoActual]
  );

  // Mensajes que quedan este mes (mismo cálculo perezoso que monthlyQuota.js).
  const limite = profile?.limiteOneBotMensual;
  const usados = profile?.usoOneBotMes === mesActualUTC() ? (profile?.usoOneBotCantidad || 0) : 0;
  const restantes = typeof limite === 'number' ? Math.max(0, limite - usados) : null;

  const nombre = (profile?.name || user?.displayName || '').split(' ')[0];
  const esperando = messages.some((m) => !m.response && m.status?.state !== 'ERROR' && m.prompt);

  // Atajo: Alt + N ("Nexus") abre o cierra; Escape lo cierra. (Ctrl+J abre
  // Descargas en Chrome y Ctrl+K ya es el Buscador Global.) `code` en vez de
  // `key` porque en Mac Alt+N escribe un carácter especial.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyN') {
        e.preventDefault();
        setIsOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen]);

  const setChatId = (id: string | null) => {
    setChatIdState(id);
    if (user) guardarChat(user.uid, id);
  };

  // Lista de conversaciones (y, una vez, la conversación única de antes).
  useEffect(() => {
    if (!user || !isOpen) return;
    migrarConversacionLegado(user.uid).catch((err) => console.error('Error migrando la conversación anterior de Nexus:', err));
    return escucharConversaciones(user.uid, setConversaciones);
  }, [user, isOpen]);

  useEffect(() => {
    if (!user || !isOpen || !chatId) { setMessages([]); return; }
    const q = query(mensajesRef(user.uid, chatId), orderBy('createTime', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }, () => setMessages([]));
    return () => unsubscribe();
  }, [user, isOpen, chatId]);

  const sendMessage = async (textToSend: string) => {
    if (!textToSend.trim() || !user || esperando) return;
    setInput('');
    let id = chatId;
    if (!id) {
      id = await crearConversacion(user.uid, textToSend, proyectoActual ? { id: proyectoActual.id, nombre: proyectoActual.nombre } : null);
      setChatId(id);
    } else {
      tocarConversacion(user.uid, id).catch(() => {});
    }
    // `currentRoute` es la ruta EN EL MOMENTO DE ENVIAR (no la de cuando
    // se montó el chat) — Nexus la usa para saber en qué proyecto está el
    // usuario ("este proyecto") y para "ya estás en esta sección".
    await addDoc(mensajesRef(user.uid, id), { prompt: textToSend, createTime: serverTimestamp(), currentRoute: location.pathname });
  };

  const nuevaConversacion = () => {
    setChatId(null);
    setVerLista(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const abrirConversacion = (id: string) => {
    setChatId(id);
    setVerLista(false);
  };

  const guardarTitulo = async (id: string) => {
    if (user && tituloEdit.trim()) await renombrarConversacion(user.uid, id, tituloEdit).catch(() => {});
    setEditandoId(null);
  };

  const eliminarConversacion = async (c: ConversacionNexus) => {
    if (!user || !window.confirm(`¿Borrar la conversación "${c.titulo}"?`)) return;
    await borrarConversacion(user.uid, c.id);
    if (c.id === chatId) setChatId(null);
  };

  // Navegación pedida por Nexus (tool `navigateTo`, resuelta server-side
  // en onebotGemini.js/geminiService.js): se ejecuta automáticamente en
  // cuanto aparece un mensaje con `pendingAction` sin consumir, y se
  // marca `executed` para no repetir la navegación si este efecto vuelve
  // a correr (p. ej. al reabrir el chat y volver a recibir el snapshot).
  useEffect(() => {
    if (!user) return;
    // `route` solo está presente en el pendingAction de navigateTo — el de
    // "prdConfirmRequired" (ver más abajo) no debe disparar una navegación,
    // se resuelve con el widget de código PRD, no con este efecto.
    const pendiente = messages.find((m) => m.pendingAction?.route && m.pendingAction.executed === false);
    if (!pendiente) return;

    if (!chatId) return;
    navigate(pendiente.pendingAction.route);
    updateDoc(mensajeRef(user.uid, chatId, pendiente.id), { 'pendingAction.executed': true }).catch((err) =>
      console.error('Error marcando navegación de Nexus como ejecutada:', err)
    );
  }, [messages, user, navigate, chatId]);

  // Enlaces internos de las respuestas ([Ver tareas](/proyecto/...)): navegan
  // dentro de la app; en pantallas chicas se cierra el chat para ver la sección.
  const navegarDesdeRespuesta = (ruta: string) => {
    navigate(ruta);
    setIsExpanded(false);
    if (window.innerWidth < 768) setIsOpen(false);
  };

  // Confirma un envío de estatus de factura a PRD con el código de
  // producción. Llama DIRECTO a onehubApi (nunca crea un mensaje de chat
  // con el código, nunca pasa por Gemini). Si el código es incorrecto se
  // deja el widget abierto para reintentar; si expiró o se agotaron los
  // intentos, se marca `executed` para forzar a rearmar la solicitud.
  const handleConfirmarPrd = async (m: any) => {
    const pendingId = m.pendingAction?.pendingId;
    const codigo = (prdCodeInputs[m.id] || '').trim();
    if (!pendingId || !codigo || !user || !chatId) return;

    setConfirmandoPrdId(m.id);
    setPrdErrors((prev) => ({ ...prev, [m.id]: '' }));
    try {
      const resultado = await confirmarEnvioEstatusFacturaPrd(pendingId, codigo);
      const msgRef = mensajeRef(user.uid, chatId, m.id);

      if (resultado.ok) {
        setPrdCodeInputs((prev) => ({ ...prev, [m.id]: '' }));
        await updateDoc(msgRef, { 'pendingAction.executed': true });
        await addDoc(mensajesRef(user.uid, chatId), {
          prompt: '🔒 Código de confirmación PRD',
          response: `✅ Envío a Producción confirmado.\n\nProyecto: ${resultado.resumen?.proyecto}\nFactura: ${resultado.resumen?.referenciaFactura}\nEstatus: ${resultado.resumen?.etiquetaEstatus}`,
          createTime: serverTimestamp()
        });
        return;
      }

      if (resultado.error === 'codigo_invalido') {
        const restantesPrd = resultado.intentosRestantes;
        setPrdErrors((prev) => ({
          ...prev,
          [m.id]: `❌ Código incorrecto.${typeof restantesPrd === 'number' ? ` Te quedan ${restantesPrd} intento(s).` : ''}`
        }));
        return;
      }

      // expirado / excedio_intentos / no_autorizado / etc.: no tiene
      // sentido seguir mostrando el widget, hay que rearmar la solicitud.
      await updateDoc(msgRef, { 'pendingAction.executed': true });
      setPrdErrors((prev) => ({
        ...prev,
        [m.id]: resultado.error === 'excedio_intentos'
          ? '❌ Se agotaron los intentos. Vuelve a pedir el envío por chat.'
          : '❌ La solicitud expiró. Vuelve a pedir el envío por chat.'
      }));
    } catch (err: any) {
      setPrdErrors((prev) => ({ ...prev, [m.id]: err.message || 'No se pudo validar el código.' }));
    } finally {
      setConfirmandoPrdId(null);
    }
  };

  // N4: el usuario confirma o cancela la acción propuesta. Se ejecuta con los
  // servicios de siempre y con sus permisos; el resultado queda en el mensaje.
  const decidirAccion = async (m: any, confirmar: boolean) => {
    if (!user || !chatId || ejecutandoId) return;
    const ref = mensajeRef(user.uid, chatId, m.id);
    if (!confirmar) {
      await updateDoc(ref, { 'pendingAction.executed': true, 'pendingAction.resultado': 'cancelada' }).catch(() => {});
      return;
    }
    setEjecutandoId(m.id);
    try {
      await ejecutarAccionNexus(m.pendingAction.accion as AccionNexus, {
        uid: user.uid,
        email: user.email || profile?.email || '',
        nombre: profile?.name || user.displayName || user.email || ''
      });
      await updateDoc(ref, { 'pendingAction.executed': true, 'pendingAction.resultado': 'ok' });
    } catch (err: any) {
      const detalle = err?.code === 'permission-denied' ? 'No tienes permiso para hacer esto en este proyecto.' : (err?.message || 'No se pudo completar.');
      await updateDoc(ref, { 'pendingAction.executed': true, 'pendingAction.resultado': 'error', 'pendingAction.detalle': detalle }).catch(() => {});
    } finally {
      setEjecutandoId(null);
    }
  };

  const copiar = async (m: any) => {
    try {
      await navigator.clipboard.writeText(m.response);
      setCopiadoId(m.id);
      setTimeout(() => setCopiadoId(null), 1500);
    } catch { /* sin permiso de portapapeles: no pasa nada */ }
  };

  const chip = (s: Sugerencia, i: number, compacto = false) => (
    <button
      key={i}
      onClick={() => sendMessage(s.text)}
      disabled={esperando}
      className={compacto
        ? 'flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-[11px] text-gray-300 hover:text-white hover:border-blue-500/40 transition-all disabled:opacity-40'
        : 'p-3.5 bg-white/5 border border-white/5 rounded-2xl text-left hover:bg-blue-600/10 hover:border-blue-500/30 transition-all group disabled:opacity-40'}
    >
      {compacto ? <>{s.icon}{s.label}</> : (
        <>
          <div className="mb-1.5 group-hover:scale-110 transition-transform">{s.icon}</div>
          <span className="text-xs font-semibold text-gray-300 group-hover:text-white">{s.label}</span>
        </>
      )}
    </button>
  );

  return (
    // Widget flotante intencionalmente siempre oscuro (como Intercom/Drift),
    // sin importar el tema del resto de la plataforma. data-theme="dark"
    // acá anida y pisa las variables CSS del tema solo para este subárbol.
    <div data-theme="dark" className="fixed bottom-3 right-3 md:bottom-6 md:right-6 z-50 flex flex-col items-end">
      {/* --- BOTÓN FLOTANTE --- */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          title="Habla con Nexus AI (Alt + N)"
          aria-label="Abrir Nexus AI"
          className="relative group bg-blue-600 p-4 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95"
        >
          <div className="absolute inset-0 rounded-2xl bg-blue-400 animate-ping opacity-20" />
          <MessageCircle size={28} className="relative z-10 text-white" />
        </button>
      )}

      {isOpen && (
        <div
          role="dialog"
          aria-label="Nexus AI"
          className={`
          bg-gray-900/95 backdrop-blur-2xl border border-white/10 rounded-3xl md:rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)]
          flex flex-col overflow-hidden transition-all duration-500 ease-in-out animate-in zoom-in duration-300
          w-[calc(100vw-1.5rem)] h-[calc(100dvh-1.5rem)]
          ${isExpanded ? 'md:w-[85vw] md:h-[85vh]' : 'md:w-[450px] md:h-[650px]'}
        `}>

          {/* HEADER */}
          <div className="px-5 py-4 bg-gradient-to-r from-purple-700/40 via-blue-600/40 to-blue-500/40 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setVerLista((v) => !v)}
                title="Conversaciones"
                aria-label="Conversaciones"
                aria-pressed={verLista}
                className={`flex-shrink-0 transition-colors ${verLista ? 'text-white' : 'text-white/60 hover:text-white'}`}
              >
                <History size={19} />
              </button>
              <div className="flex flex-col min-w-0">
                <h3 className="font-bold text-white text-sm tracking-tight truncate">
                  {verLista ? 'Conversaciones' : (conversaciones.find((c) => c.id === chatId)?.titulo || 'Nexus AI')}
                </h3>
                <span className="text-[10px] text-blue-200/80 truncate">
                  {proyectoActual ? `En: ${proyectoActual.nombre}` : 'Tus proyectos en OneHub'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <button onClick={nuevaConversacion} title="Nueva conversación" aria-label="Nueva conversación" className="text-white/60 hover:text-white transition-colors">
                <Plus size={19} />
              </button>
              <button onClick={() => setIsExpanded(!isExpanded)} title={isExpanded ? 'Achicar' : 'Agrandar'} aria-label={isExpanded ? 'Achicar' : 'Agrandar'} className="hidden md:block text-white/60 hover:text-white transition-colors">
                {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button onClick={() => setIsOpen(false)} title="Cerrar (Esc)" aria-label="Cerrar" className="text-white/60 hover:text-white">
                <X size={22} />
              </button>
            </div>
          </div>

          {verLista && (
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              <button onClick={() => setVerLista(false)} className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-400 hover:text-white">
                <ArrowLeft size={13} /> Volver al chat
              </button>
              {conversaciones.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-10">Todavía no tienes conversaciones guardadas.</p>
              )}
              {conversaciones.map((c) => (
                <div
                  key={c.id}
                  className={`group flex items-center gap-2 rounded-xl border px-3 py-2.5 transition-all ${
                    c.id === chatId ? 'border-blue-500/50 bg-blue-600/10' : 'border-white/5 bg-white/5 hover:border-white/15'
                  }`}
                >
                  {editandoId === c.id ? (
                    <input
                      autoFocus
                      value={tituloEdit}
                      onChange={(e) => setTituloEdit(e.target.value)}
                      onBlur={() => guardarTitulo(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') guardarTitulo(c.id);
                        if (e.key === 'Escape') { e.stopPropagation(); setEditandoId(null); }
                      }}
                      maxLength={80}
                      className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-blue-500"
                    />
                  ) : (
                    <button onClick={() => abrirConversacion(c.id)} className="flex-1 min-w-0 text-left">
                      <p className="text-sm text-white truncate">{c.titulo}</p>
                      <p className="text-[10px] text-gray-500 truncate">
                        {fechaCorta(c.actualizadoEn)}{c.proyectoNombre ? ` · ${c.proyectoNombre}` : ''}
                      </p>
                    </button>
                  )}
                  {editandoId !== c.id && (
                    <div className="flex items-center gap-1 opacity-60 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button
                        onClick={() => { setEditandoId(c.id); setTituloEdit(c.titulo); }}
                        title="Renombrar"
                        aria-label="Renombrar conversación"
                        className="p-1.5 text-gray-400 hover:text-white"
                      >
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => eliminarConversacion(c)} title="Borrar" aria-label="Borrar conversación" className="p-1.5 text-gray-400 hover:text-red-400">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ÁREA DE MENSAJES */}
          <div className={`flex-1 overflow-y-auto p-4 md:p-6 space-y-5 scrollbar-thin scrollbar-thumb-gray-700 ${verLista ? 'hidden' : ''}`}>
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-6 animate-in fade-in duration-1000">
                <div className="w-16 h-16 bg-gradient-to-tr from-purple-600 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl rotate-12">
                  <Sparkles size={32} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white text-lg font-bold">Hola{nombre ? ` ${nombre}` : ''}, soy Nexus</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    {proyectoActual
                      ? <>Pregúntame lo que quieras de <span className="text-white">{proyectoActual.nombre}</span>.</>
                      : 'Te ayudo con tus pendientes, tus proyectos y a moverte por OneHub.'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 w-full">
                  {sugerencias.map((s, i) => chip(s, i))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className="space-y-3">
                <div className="flex justify-end animate-in slide-in-from-right-2">
                  <div className="bg-blue-600 text-white p-3 px-4 rounded-2xl rounded-tr-none max-w-[85%] text-sm shadow-lg shadow-blue-900/20 whitespace-pre-wrap">
                    {m.prompt}
                  </div>
                </div>

                {m.response && (
                  <div className="flex justify-start animate-in slide-in-from-left-2 group">
                    <div className="relative bg-white/5 border border-white/10 text-gray-200 p-4 rounded-2xl rounded-tl-none max-w-[90%] backdrop-blur-md shadow-xl">
                      <MarkdownNexus texto={m.response} navegar={navegarDesdeRespuesta} />
                      <button
                        onClick={() => copiar(m)}
                        title="Copiar respuesta"
                        aria-label="Copiar respuesta"
                        className="absolute -bottom-3 right-3 p-1.5 rounded-lg bg-gray-800 border border-white/10 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      >
                        {copiadoId === m.id ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                  </div>
                )}

                {!m.response && m.status?.state === 'ERROR' && (
                  <div className="flex justify-start">
                    <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-2xl rounded-tl-none max-w-[85%] text-sm">
                      {m.status.error || 'No se pudo generar una respuesta.'}{' '}
                      <button onClick={() => sendMessage(m.prompt)} disabled={esperando} className="underline font-semibold disabled:opacity-40">Reintentar</button>
                    </div>
                  </div>
                )}

                {!m.response && m.status?.state !== 'ERROR' && (
                  <div className="flex justify-start" aria-live="polite">
                    <div className="bg-white/5 border border-white/10 text-gray-400 px-4 py-3 rounded-2xl rounded-tl-none text-xs flex items-center gap-2">
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce [animation-delay:300ms]" />
                      </span>
                      Nexus está consultando…
                    </div>
                  </div>
                )}

                {m.pendingAction?.type === 'accionNexus' && (() => {
                  const accion = m.pendingAction.accion as AccionNexus;
                  const etiqueta = ETIQUETA_ACCION[accion?.tipo];
                  if (!etiqueta) return null;
                  if (!m.pendingAction.executed) {
                    return (
                      <div className="flex justify-start gap-2 pl-1">
                        <button
                          onClick={() => decidirAccion(m, true)}
                          disabled={!!ejecutandoId}
                          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors"
                        >
                          <Check size={14} /> {ejecutandoId === m.id ? 'Guardando…' : `Confirmar: ${etiqueta.boton}`}
                        </button>
                        <button
                          onClick={() => decidirAccion(m, false)}
                          disabled={!!ejecutandoId}
                          className="text-xs font-semibold text-gray-400 hover:text-white px-3 py-2 rounded-xl border border-white/10 disabled:opacity-50"
                        >
                          Cancelar
                        </button>
                      </div>
                    );
                  }
                  const r = m.pendingAction.resultado;
                  return (
                    <div className={`flex items-center gap-2 pl-1 text-xs ${r === 'ok' ? 'text-emerald-400' : r === 'error' ? 'text-red-400' : 'text-gray-500'}`}>
                      {r === 'ok' ? <CircleCheck size={14} /> : <CircleX size={14} />}
                      {r === 'ok' ? (
                        <>
                          {etiqueta.hecho}.
                          <button onClick={() => navegarDesdeRespuesta(`/proyecto/${accion.projectId}/${etiqueta.ruta}`)} className="underline font-semibold">Ver</button>
                        </>
                      ) : r === 'error' ? `No se pudo: ${m.pendingAction.detalle || 'error'}` : 'Cancelado. No se hizo ningún cambio.'}
                    </div>
                  );
                })()}

                {m.pendingAction?.type === 'prdConfirmRequired' && !m.pendingAction.executed && (
                  <div className="flex justify-start animate-in slide-in-from-left-2">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 max-w-[85%] space-y-3">
                      <div className="flex items-center gap-2 text-red-400 text-xs font-bold uppercase tracking-wide">
                        <ShieldAlert size={14} /> Confirmación de Producción
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          value={prdCodeInputs[m.id] || ''}
                          onChange={(e) => setPrdCodeInputs((prev) => ({ ...prev, [m.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && !confirmandoPrdId && handleConfirmarPrd(m)}
                          placeholder="Código PRD"
                          disabled={confirmandoPrdId === m.id}
                          className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white text-center tracking-[0.3em] outline-none focus:border-red-500 transition-colors"
                        />
                        <button
                          onClick={() => handleConfirmarPrd(m)}
                          disabled={confirmandoPrdId === m.id || !(prdCodeInputs[m.id] || '').trim()}
                          className="flex items-center gap-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-bold px-4 rounded-xl transition-colors"
                        >
                          <Check size={14} /> {confirmandoPrdId === m.id ? 'Validando...' : 'Confirmar'}
                        </button>
                      </div>
                      {prdErrors[m.id] && (
                        <p className="text-xs text-red-400">{prdErrors[m.id]}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div ref={scrollRef} />
          </div>

          {/* SUGERENCIAS + INPUT */}
          <div className={`bg-white/5 border-t border-white/10 ${verLista ? 'hidden' : ''}`}>
            {messages.length > 0 && (
              <div className="flex gap-2 overflow-x-auto px-4 pt-3 pb-1 scrollbar-thin">
                {sugerencias.map((s, i) => chip(s, i, true))}
              </div>
            )}
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
              className="px-4 pt-2 pb-2 flex gap-3 items-center"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={proyectoActual ? `Pregunta sobre ${proyectoActual.nombre}…` : 'Pregúntale a Nexus: pendientes, avance, cómo hacer algo…'}
                aria-label="Mensaje para Nexus"
                className="flex-1 min-w-0 bg-gray-800/40 border border-white/10 rounded-2xl px-4 py-3.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
              />
              <button
                type="submit"
                disabled={!input.trim() || esperando}
                aria-label="Enviar"
                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white p-3.5 rounded-2xl transition-all shadow-lg shadow-blue-900/40 active:scale-90"
              >
                <Send size={20} />
              </button>
            </form>
            <p className="px-5 pb-3 text-[10px] text-gray-500 flex justify-between gap-2">
              <span className="hidden md:inline">Alt + N para abrir o cerrar</span>
              {restantes !== null && (
                <span className={restantes <= 5 ? 'text-amber-400' : ''}>
                  Te quedan {restantes} de {limite} mensajes este mes
                </span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
